import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import AppLayout from "@/components/AppLayout"
import { ProtectedRoute } from "@/components/ProtectedRoute"
import { RechtRoute } from "@/components/RechtRoute"
import Dashboard from "@/pages/Dashboard"
import Login from "@/pages/Login"
import KundeDetail from "@/pages/KundeDetail"
import KundenList from "@/pages/KundenList"
import EmailsList from "@/pages/EmailsList"
import EmailDetail from "@/pages/EmailDetail"
import WhatsAppPage from "@/pages/WhatsAppPage"
import AngebotePage from "@/pages/AngebotePage"
import AngebotDetail from "@/pages/AngebotDetail"
import SipgateCallsList from "@/pages/SipgateCallsList"
import MeitiCallsList from "@/pages/MeitiCallsList"
import ContactsList from "@/pages/ContactsList"
import TenantsList from "@/pages/TenantsList"
import TasksList from "@/pages/TasksList"
import InterneAufgabenPage from "@/pages/InterneAufgabenPage"
import VertriebPage from "@/pages/VertriebPage"
import DienstplanPage from "@/pages/DienstplanPage"
import WissensdatenbankPage from "@/pages/WissensdatenbankPage"
import KontoPage from "@/pages/KontoPage"
import ZugaengePage from "@/pages/ZugaengePage"
import CalendarView from "@/pages/CalendarView"
import MeisterAI from "@/pages/MeisterAI"
import SecurityPage from "@/pages/SecurityPage"
import ProjectsList from "@/pages/ProjectsList"
import SearchPage from "@/pages/SearchPage"
import ProjectDetailPage from "@/pages/ProjectDetail"
import TicketsList from "@/pages/TicketsList"
import ContactDetail from "@/pages/ContactDetail"
import SipgateCallDetail from "@/pages/SipgateCallDetail"
import MeitiCallDetail from "@/pages/MeitiCallDetail"
import VerbessernPage from "@/pages/VerbessernPage"
import ChatbotPage from "@/pages/ChatbotPage"
import ReviewPage from "@/pages/ReviewPage"
import ContextCenter from "@/pages/ContextCenter"
import TenantEdit from "@/pages/TenantEdit"
import KiTelefonPage from "@/pages/KiTelefonPage"
import KiTelefonNeuPage from "@/pages/KiTelefonNeuPage"
import TodoPage from "@/pages/TodoPage"
import ActivityLogPage from "@/pages/ActivityLogPage"
import KostenPage from "@/pages/KostenPage"
import ControllingPage from "@/pages/ControllingPage"
import ControllingAnnahmenPage from "@/pages/ControllingAnnahmenPage"
import KundenbestaetigungPage from "@/pages/KundenbestaetigungPage"
import AutoSwitchesPage from "@/pages/AutoSwitchesPage"
import BacklogPage from "@/pages/BacklogPage"
import { UndoSendToast } from "@/components/UndoSendToast"
import { FailedSendAlert } from "@/components/FailedSendAlert"
import { ErrorBoundary } from "@/components/ErrorBoundary"
import { emailQueryDefaults } from "@/lib/emailQuery"

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 30,
    },
  },
})

// 🔴 Die Mail-Abfrage gehoert dem Client, nicht den Komponenten — sonst nimmt
// sie sich der naechste Leser des Schluessels gegenseitig weg („Missing
// queryFn", dreimal gemeldet). Siehe `lib/emailQuery.ts`.
emailQueryDefaults(queryClient)

export default function App() {
  return (
    <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <UndoSendToast />
      <BrowserRouter>
        {/* Innerhalb des Routers, weil die Karte auf die betroffene Mail verlinkt. */}
        <FailedSendAlert />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="todo" element={<TodoPage />} />
            <Route path="meister" element={<MeisterAI />} />
            <Route path="kunden" element={<KundenList />} />
            <Route path="kunde/:slug" element={<KundeDetail />} />
            <Route path="emails" element={<EmailsList />} />
            <Route path="emails/:id" element={<EmailDetail />} />
            <Route path="sipgate" element={<SipgateCallsList />} />
            <Route path="whatsapp" element={<WhatsAppPage />} />
            <Route path="angebote" element={<RechtRoute recht="angebote"><AngebotePage /></RechtRoute>} />
            <Route path="angebote/:id" element={<RechtRoute recht="angebote"><AngebotDetail /></RechtRoute>} />
            <Route path="sipgate/:id" element={<SipgateCallDetail />} />
            <Route path="calls" element={<MeitiCallsList />} />
            <Route path="calls/:id" element={<MeitiCallDetail />} />
            <Route path="contacts" element={<ContactsList />} />
            <Route path="contacts/:id" element={<ContactDetail />} />
            <Route path="projekte" element={<SearchPage />} />
            {/* Objektliste (fortytools) — bleibt erreichbar, die Suche
                hat den Reiter uebernommen. */}
            <Route path="objekte" element={<ProjectsList />} />
            <Route path="projekte/:id" element={<ProjectDetailPage />} />
            <Route path="tenants" element={<RechtRoute recht="mandanten"><TenantsList /></RechtRoute>} />
            <Route path="tenants/:slug" element={<RechtRoute recht="mandanten"><TenantEdit /></RechtRoute>} />
            {/* 02.11.2026: die Arbeitsliste ersetzt die alte Tabelle; die bleibt
                als Rückfall unter /ki-telefon-alt. /ki-telefon-neu war die Vorschau. */}
            <Route path="ki-telefon" element={<KiTelefonNeuPage />} />
            <Route path="ki-telefon/:callId" element={<KiTelefonNeuPage />} />
            <Route path="ki-telefon-alt" element={<KiTelefonPage />} />
            <Route path="ki-telefon-neu" element={<Navigate to="/ki-telefon" replace />} />
            <Route path="tasks" element={<TasksList />} />
            <Route path="interne-aufgaben" element={<InterneAufgabenPage />} />
            <Route path="vertrieb" element={<RechtRoute recht="vertrieb"><VertriebPage /></RechtRoute>} />
            <Route path="dienstplan" element={<DienstplanPage />} />
            <Route path="wissensdatenbank" element={<WissensdatenbankPage />} />
            <Route path="konto" element={<KontoPage />} />
            <Route path="zugaenge" element={<RechtRoute recht="zugaenge"><ZugaengePage /></RechtRoute>} />
            <Route path="calendar" element={<CalendarView />} />
            <Route path="tickets" element={<TicketsList />} />
            {/* /status: Backward-Compat-Redirect — System-Status ist jetzt
                Teil von /security (klappbare Sektion am Ende). */}
            <Route path="status" element={<Navigate to="/security" replace />} />
            <Route path="security" element={<RechtRoute recht="sicherheit"><SecurityPage /></RechtRoute>} />
            <Route path="aktivitaet" element={<ActivityLogPage />} />
            <Route path="kosten" element={<RechtRoute recht="kosten"><KostenPage /></RechtRoute>} />
            <Route path="controlling" element={<RechtRoute recht="controlling"><ControllingPage /></RechtRoute>} />
            <Route path="controlling/annahmen" element={<RechtRoute recht="controlling"><ControllingAnnahmenPage /></RechtRoute>} />
            <Route path="rueckmeldungen" element={<KundenbestaetigungPage />} />
            <Route path="auto-switches" element={<AutoSwitchesPage />} />
            <Route path="backlog" element={<RechtRoute recht="backlog"><BacklogPage /></RechtRoute>} />
            {/* „Verbessern" (Paul 11.09.2026): Wege, Vorschlagsqualitaet und
                Rueckkopplung unter einem Menuepunkt. Die alten Pfade bleiben als
                Weiterleitung auf den passenden Reiter. */}
            <Route path="verbessern" element={<VerbessernPage />} />
            <Route path="wege" element={<Navigate to="/verbessern?tab=wege" replace />} />   {/* #44 */}
            <Route path="vorschlagsqualitaet" element={<Navigate to="/verbessern?tab=vorschlagsqualitaet" replace />} />   {/* #64 */}
            <Route path="rueckkopplung" element={<Navigate to="/verbessern?tab=rueckkopplung" replace />} />   {/* #64 Teil B */}
            <Route path="projektplan" element={<Navigate to="/verbessern?tab=projektplan" replace />} />   {/* Strategie 24.09.2026 */}
            <Route path="chat" element={<ChatbotPage />} />
            <Route path="review" element={<ReviewPage />} />
            <Route path="context" element={<ContextCenter />} />
            <Route path="inbox" element={<Navigate to="/review" replace />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
    </ErrorBoundary>
  )
}
